import React, { useState } from 'react';

// The client screen, laid out like Joist's Edit Client (built 2026-08-18 from
// Julian's screenshot): a full screen with a back / title / Done bar, a name
// row with My Clients beside it, and grouped sections — Basic Info, Billing
// Address, the switch, Service Address.
//
// It replaced a bottom sheet of stacked labelled boxes. The layout is Joist's
// because that is the form Julian fills in every day and he should not have to
// learn a second shape for the same job; the type and the palette are QPS's,
// the same trade the tech portal made — Cormorant headings, camel and blue,
// no bold, no emojis.
//
// Two things the old sheet had nowhere to put, and Joist asks for by name: a
// second phone number, and a billing address that is a real address — street,
// unit, city, state, ZIP — rather than one line of free text. An owner who
// lives out of state, or a rental billed to the landlord, now writes down the
// way an invoice is actually addressed.

// Which keys on the form each address block writes to. The billing block is
// the one always on screen: with the switch off it IS the service address (see
// handleAddClient, which copies it across on save), and with the switch on the
// service address gets its own block underneath.
const BILLING_KEYS = {
  a1: 'billingAddress', a2: 'billingAddress2',
  city: 'billingCity', state: 'billingState', zip: 'billingZip',
};
const SERVICE_KEYS = { a1: 'address', a2: 'address2', city: 'city', state: 'state', zip: 'zip' };

const AddressRows = ({ keys, form, set }) => (
  <>
    <input className="cl-input" value={form[keys.a1] || ''} placeholder="Address #1"
      autoComplete="off" onChange={e => set({ [keys.a1]: e.target.value })} />
    <input className="cl-input" value={form[keys.a2] || ''} placeholder="Address #2"
      autoComplete="off" onChange={e => set({ [keys.a2]: e.target.value })} />
    <input className="cl-input" value={form[keys.city] || ''} placeholder="City"
      autoComplete="off" onChange={e => set({ [keys.city]: e.target.value })} />
    <input className="cl-input" value={form[keys.state] || ''} placeholder="State/Province"
      autoComplete="off" onChange={e => set({ [keys.state]: e.target.value })} />
    <input className="cl-input" value={form[keys.zip] || ''} placeholder="Zip/Postal Code"
      inputMode="numeric" autoComplete="off" onChange={e => set({ [keys.zip]: e.target.value })} />
  </>
);

export default function ClientEditor({
  form, setForm, onSave, saving, clients, onPickClient, link, onCopyLink, onClose,
}) {
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');

  const set = (patch) => setForm(p => ({ ...p, ...patch }));
  const isEdit = Boolean(form._id);
  const different = form.billingSameAsService === false;

  // Flicking the switch on hands the address already typed to the bill — which
  // is the block it was typed in — and leaves the service block empty to be
  // filled. Flicking it back off gives it to both again. Either way nothing
  // that was typed disappears.
  const setDifferent = (on) => {
    if (on) {
      set({
        billingSameAsService: false,
        address: '', address2: '', city: '', state: '', zip: '',
      });
    } else {
      set({ billingSameAsService: true });
    }
  };

  const q = query.trim().toLowerCase();
  const matches = (clients || []).filter(c => !q
    || (c.name || '').toLowerCase().includes(q)
    || (c.phone || '').toLowerCase().includes(q)
    || (c.email || '').toLowerCase().includes(q));

  return (
    <>
      <div className="modal active" style={{ alignItems: 'stretch' }}>
        <div className="cl-editor">
          <div className="cl-bar">
            <button className="cl-bar-btn" onClick={onClose}>‹ Clients</button>
            <span className="cl-bar-title">{isEdit ? 'Edit Client' : 'New Client'}</span>
            <button className="cl-bar-btn" disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Done'}</button>
          </div>

          <div className="cl-body">
            {/* Name, and beside it the customers already on file. Adding
                somebody who is in there already is the mistake this button
                exists to stop — tapping a name opens that client instead. */}
            <div className="cl-name-row">
              <input className="cl-name" value={form.name || ''} placeholder="Name"
                onChange={e => set({ name: e.target.value })} />
              {!isEdit && (
                <button className={`cl-pill${picking ? ' open' : ''}`} onClick={() => setPicking(p => !p)}>
                  My Clients
                </button>
              )}
            </div>

            {picking && (
              <div className="cl-picker">
                <input className="cl-picker-search" value={query} placeholder="Search clients"
                  onChange={e => setQuery(e.target.value)} />
                {matches.length === 0 ? (
                  <div className="cl-picker-empty">
                    {(clients || []).length ? 'No client by that name.' : 'No clients on file yet.'}
                  </div>
                ) : matches.map(c => (
                  <button key={c._id} className="cl-picker-row"
                    onClick={() => { setPicking(false); setQuery(''); onPickClient(c); }}>
                    <span>{c.name}</span>
                    <span className="cl-picker-sub">{c.phone || c.email || ''}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="cl-sec">Basic Info</div>
            <div className="cl-row">
              <span className="cl-label">Email</span>
              <input className="cl-value" type="email" value={form.email || ''} placeholder="Email"
                autoComplete="off" onChange={e => set({ email: e.target.value })} />
            </div>
            <div className="cl-row">
              <span className="cl-label">Phone #1</span>
              <input className="cl-value" type="tel" value={form.phone || ''} placeholder="Mobile Number"
                autoComplete="off" onChange={e => set({ phone: e.target.value })} />
            </div>
            <div className="cl-row">
              <span className="cl-label">Phone #2</span>
              <input className="cl-value" type="tel" value={form.phone2 || ''} placeholder="Home Number"
                autoComplete="off" onChange={e => set({ phone2: e.target.value })} />
            </div>
            {/* One or the other, not both — plenty of homeowners have no email,
                and plenty give an email and never answer the phone. */}
            <div className="cl-hint">
              A phone number or an email, whichever they gave you. With an email on file they also get
              a link to their own portal.
            </div>

            <div className="cl-sec">Billing Address</div>
            <div className="cl-sec-sub">Address where the bill will be sent.</div>
            <AddressRows keys={BILLING_KEYS} form={form} set={set} />

            <label className="cl-switch-row">
              <span>Billing address is different than service address.</span>
              <span className={`dopt-switch${different ? ' on' : ''}`}>
                <input
                  type="checkbox"
                  checked={different}
                  onChange={e => setDifferent(e.target.checked)}
                  // Visually hidden rather than display:none — Safari will not
                  // toggle a display:none control from a label.
                  style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
                />
                <span className="dopt-knob"></span>
              </span>
            </label>

            {different && (
              <>
                <div className="cl-sec">Service Address</div>
                <div className="cl-sec-sub">Address where the work will be done.</div>
                <AddressRows keys={SERVICE_KEYS} form={form} set={set} />
              </>
            )}

            <div className="cl-sec">Notes</div>
            <textarea className="cl-notes" rows={4} value={form.notes || ''}
              placeholder="Gate code, the dog, who to ask for — anything you would otherwise have to remember."
              onChange={e => set({ notes: e.target.value })} />

            {/* Off by default: somebody who has just given their details on the
                phone is not expecting a password email a minute later. On an
                edit this only does anything when the email itself changed —
                that is the only case a fresh link is minted. */}
            {form.email?.trim() && (
              <>
                <div className="cl-sec">Portal Access</div>
                <label className="cl-switch-row">
                  <span>Email them a link to set their own password.</span>
                  <span className={`dopt-switch${form.sendInvite === true ? ' on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={form.sendInvite === true}
                      onChange={e => set({ sendInvite: e.target.checked })}
                      style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
                    />
                    <span className="dopt-knob"></span>
                  </span>
                </label>
                <div className="cl-hint">
                  Either way you get the link on screen to send yourself — no password is ever emailed.
                </div>
              </>
            )}

            {link && (
              <div className="cl-link">
                <strong>Their set-password link — good for 7 days</strong>
                <span>{link}</span>
                <button className="btn btn-outline btn-sm" onClick={onCopyLink}>Copy link</button>
              </div>
            )}

            <div className="cl-foot">
              <button className="btn btn-primary btn-lg" disabled={saving} onClick={onSave}>
                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Client'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="overlay active" onClick={onClose}></div>
    </>
  );
}
